/**
 * 🔴 **导航清单的唯一处** —— 侧栏渲染、当前项高亮、顶栏页面标题都从这里读。
 *
 * ## 为什么要单独一个文件
 * 导航项在 3 个地方被用到（侧栏渲染 / 当前项高亮 / 顶栏标题）。若各写一份，
 * 加一个页面就要改 3 处，而**漏改不会报错**（只是那一项不高亮）—— 最典型的静默不一致。
 *
 * ## 🔴 三组 **12 项** = 信息架构的**全量**（`DR-025` 批 99 修订：家底组加「投资」—— `DR-059` D1）
 *
 * | 组 | 项 |
 * |---|---|
 * | 日常（5） | 今天 · 流水 · 账单导入 · 计划 · 提醒中心 |
 * | 家底（7） | 资产 · 账户 · **投资** · 债权债务 · 储蓄目标 · 报表 · 保单 |
 * | 协作（1） | 家庭 |
 *
 * - **设置入口在顶栏，不占导航位**（`AC-O1.6`）；**改名不动 key**（铁律 12）。
 * - 组的 DOM 位置**只在共享层设计资产里定义**（`assets/_chrome.js` 的 `NAV`）—— 本文件是它的 TS 镜像。
 *
 * ## ⚠️ P0 只渲染**已实现**的项，不画灰项
 * `path === undefined` ⇒ 该域尚无页面 ⇒ **侧栏不渲染它**（灰按钮点不动，比没有更糟）。
 * 但**清单保留全 10 项** —— 它是信息架构的真相源，实现一个点亮一个，
 * 这样"还差哪些"始终**可数**（不靠记忆，也不靠 grep 原型）。
 */

import {
  AccountBalanceWalletOutlined,
  BalanceOutlined,
  CalendarMonthOutlined,
  GroupsOutlined,
  HomeOutlined,
  InsertChartOutlined,
  NotificationsNoneOutlined,
  ReceiptLongOutlined,
  SavingsOutlined,
  ShieldOutlined,
  TrackChangesOutlined,
  TrendingUpOutlined,
  UploadFileOutlined,
} from '@mui/icons-material';
import type { ReactNode } from 'react';

export type NavKey =
  // 日常
  | 'today'
  | 'ledger'
  | 'import'
  | 'plan'
  | 'remind'
  // 家底
  | 'wealth'
  | 'accounts'
  // 🆕 批 99 B2：投资（S 域 · 第 19 域）—— `DR-059` D1：导航 11 → 12 项
  | 'invest'
  | 'debt'
  | 'goal'
  | 'report'
  // 协作
  | 'family'
  // 🆕 暂挂（见下方 `provisional`）
  | 'policies';

export interface NavItem {
  key: NavKey;
  label: string;
  path?: string;
  icon: ReactNode;
  /**
   * 🆕 **暂挂项**：按设计它**不该占导航位**，但 P0 已把它独立成页面 ⇒ 暂列此处，**以免入口丢失**。
   * 🔴 每写一个 `provisional` 都必须写明**收拢时机** —— 否则"暂挂"会悄悄变成永久。
   */
  provisional?: string;
}

export interface NavGroup {
  /** 组名（纯展示；**组名不是 key**，改名不影响任何逻辑） */
  group: string;
  items: readonly NavItem[];
}

/**
 * 🔴 **导航全量**（顺序 = 展示顺序）。
 *
 * ⚠️ 只有 `policies` 一项带 `provisional` —— 它是**与设计的显式分歧**（已登记）：
 * `DR-025` 定义「资产（账户）= 家底总览 + 8 类账户卡 + 重大资产 + **社保与保险 4 面板**」
 * ⇒ 保单按设计应是**资产页内的一个面板**，不是独立导航目的地；
 * 而 ⑰/⑱ 批已把保单台账独立成页（`/policies`）⇒ 暂挂在家底组，**收拢时机 = 资产页落地时**。
 */
export const NAV: readonly NavGroup[] = [
  {
    group: '日常',
    items: [
      { key: 'today', label: '今天', path: '/', icon: <HomeOutlined /> },
      // 🆕 ㊼ 批放出：流水页（C 域 C1）—— 日常主入口
      { key: 'ledger', label: '流水', path: '/journal', icon: <ReceiptLongOutlined /> },
      // 🆕 批 86 放出：账单导入页（D 域 D1–D5）
      { key: 'import', label: '账单导入', path: '/import', icon: <UploadFileOutlined /> },
      // 🆕 F 域批放出：计划页（F2 未来必付日历 + 周期规则启停）
      { key: 'plan', label: '计划', path: '/plan', icon: <CalendarMonthOutlined /> },
      // 🆕 H 域批放出：提醒中心（H2 站内兜底通道 —— 不依赖任何授权，始终可用）
      { key: 'remind', label: '提醒中心', path: '/reminders', icon: <NotificationsNoneOutlined /> },
    ],
  },
  {
    group: '家底',
    items: [
      // 🆕 批 95：资产 = 汇总（净资产/资产/负债）+ 去向入口；账户 = 全量台账（按分类）
      { key: 'wealth', label: '资产', path: '/assets', icon: <SavingsOutlined /> },
      { key: 'accounts', label: '账户', path: '/accounts', icon: <AccountBalanceWalletOutlined /> },
      /*
        🆕 批 99 B2 放出：**投资**（S 域第 19 域 · `DR-059` D1：导航 11 → 12 项）。

        🔴 位置固定在「账户」之后：账户只回答"这个平台里还有多少没买入的现金"，
        持仓与买卖全在这一页（规格 §47「余额与持仓市值分列」—— 数据层早就成立，
        只是界面从来没兑现）。

        ⚠️ 数字键：按已实现项的出现顺序编 1–9 ⇒「投资」拿到 `8`，
        原本 `8` 的「债权债务」变 `9`，`报表` / `保单` 从此**没有**数字键
        （宁可没键位，不让一个数字指两页 —— `keymap.ts` 文件头）。
      */
      { key: 'invest', label: '投资', path: '/investments', icon: <TrendingUpOutlined /> },
      // 批 94 放出：债权债务页（信用卡账单 + contra 五类 + 预付/待摊）
      { key: 'debt', label: '债权债务', path: '/debts', icon: <BalanceOutlined /> },
      // 🆕 批 85 放出：储蓄目标页（I 域 I1–I3）
      { key: 'goal', label: '储蓄目标', path: '/goals', icon: <TrackChangesOutlined /> },
      // 🆕 K 域批放出：报表页（K1–K4 四段全时间轴）
      { key: 'report', label: '报表', path: '/report', icon: <InsertChartOutlined /> },
      {
        key: 'policies',
        label: '保单',
        path: '/policies',
        icon: <ShieldOutlined />,
        provisional: '按 DR-025 应为「资产」页的面板；收拢时机 = 资产页落地时',
      },
    ],
  },
  {
    group: '协作',
    items: [{ key: 'family', label: '家庭', icon: <GroupsOutlined /> }],
  },
];

/** 只保留**已实现**的项（P0 不画灰项）；空组不返回（侧栏不出现空标题） */
export function implementedGroups(): readonly { group: string; items: readonly NavItem[] }[] {
  return NAV.map((g) => ({ group: g.group, items: g.items.filter((i) => i.path !== undefined) })).filter(
    (g) => g.items.length > 0,
  );
}

/**
 * 顶栏标题：按当前路由找导航项；非导航目的地（记一笔 / 设置）单独给名字。
 * ⚠️ 返回 `{ title, sub? }` —— `sub` 留给页面自己补充（如账本名），此处只给主标题。
 *
 * 🔴 `/settings` **不在 `NAV` 里**（`AC-O1.6`：设置入口在顶栏、不占导航位），
 *    但它是**真实路由** ⇒ 顶栏标题仍必须认得它，否则进设置页标题会退化成「家盈」。
 *    ⇒ 这里显式补一条，而不是把它塞回 `NAV`（那会让侧栏长出第 11 个导航位）。
 */
export function titleOf(pathname: string): string {
  for (const g of NAV) {
    for (const it of g.items) {
      if (it.path !== undefined && it.path === pathname) return it.label;
    }
  }
  if (pathname === '/new') return '记一笔';
  if (pathname === '/settings') return '家庭与设置';
  if (pathname.startsWith('/installments')) return '信用卡分期';
  return '家盈';
}
